import { Component, DestroyRef, HostListener, inject, Inject, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateHolidayService } from '../../core/services/date-holiday.service';
import { TimerecordsService } from '../../core/services/timerecords.service';
import moment from 'moment';
import { catchError, firstValueFrom, map, Observable, of } from 'rxjs';
import { ProjectsActivitiesService } from '../../core/services/projects-activities.service';
import {
  ProjectSelectionDialogResult,
  ProjectSelectionModalComponent,
} from '../../shared/components/modal/project-selection-modal.component';
import { TimeRecord } from '../../models/timerecord.model';
import { ProjectWithActivities } from '../../models/project.model';
import { ActivityView } from '../../models/activity.model';
import { ModalService } from '../../core/services/modal.service';
import { DialogService } from '../../core/services/dialog.service';
import { FeatureFlagsService } from '../../core/services/feature-flags.service';

@Component({
  selector: 'app-ng-time',
  template: require('./ng-time.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-time.component.scss').default],
  standalone: false,
})
export class NgTimeComponent implements OnInit {
  selectedDate = moment().startOf('day').isoWeekday(1);
  periodLength = 7;
  weekList;
  periodTotal = 0;
  workHours = 0;
  projects: ProjectWithActivities[] = [];
  timeRecords: TimeRecord[] = [];

  monthTotal = {
    month: '',
    rapporterat: 0,
    total: 0,
    fardig: 0,
  };
  lastMonthTotal = {
    month: '',
    rapporterat: 0,
    total: 0,
    fardig: 0,
  };

  periodStatistics = {
    normalTimePercent: 0,
    overTimePercent: 0,
    missingTimePercent: 0,
    normalTime: 0,
    overTime: 0,
    missingTime: 0,
  };

  weekHeaderNg: { days: any[]; workHours: number } = { days: [], workHours: 0 };
  currTimeTable: any[] = [];
  timeNotesEnabled = false;
  projectBudgetInTimeEnabled = false;
  activeNoteEditor: { rowIndex: number; dayIndex: number } | null = null;
  activeNoteDraft = '';
  activeNotePopoverTop: number | null = null;

  private destroyRef = inject(DestroyRef);

  constructor(
    @Inject(TimerecordsService) private timerecordsService: TimerecordsService,
    @Inject(ProjectsActivitiesService) private projectsActivitiesService: ProjectsActivitiesService,
    @Inject(DateHolidayService) private dateHolidayService: DateHolidayService,
    @Inject(ModalService) private modalService: ModalService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(FeatureFlagsService) private featureFlags: FeatureFlagsService
  ) {}

  ngOnInit() {
    this.featureFlags
      .getFlags$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((flags) => {
        this.timeNotesEnabled = !!flags.timeNotes;
        this.projectBudgetInTimeEnabled = !!flags.projectBudgetInTime;
        if (!this.timeNotesEnabled) {
          this.closeNoteEditor();
        }
      });
    this.updateData();
  }

  updateData() {
    this.weekList = this.dateHolidayService.getWeekList(this.selectedDate, this.periodLength);
    this.buildWeekHeader();
    this.fetchData();
  }

  onDateChange(date: moment.Moment) {
    this.selectedDate = date.clone().startOf('day').isoWeekday(1);
    this.updateData();
  }

  buildWeekHeader() {
    const header = [{ header: 'Projekt' }, { header: 'Aktivitet' }];
    this.weekHeaderNg.days = header.concat(this.weekList);
    this.weekHeaderNg.workHours = this.calculateWorkHours(
      this.selectedDate,
      this.selectedDate.clone().add(this.periodLength - 1, 'day')
    );
    console.log('weekHeaderNg:', this.weekHeaderNg);
  }

  buildTimeTable(records: TimeRecord[]) {
    const groupedRecords = new Map<string, any>();

    records.forEach((record) => {
      const key = `${record.project_id}-${record.activity_id}`;
      if (!groupedRecords.has(key)) {
        groupedRecords.set(key, {
          header: [
            {
              id: record.project_id,
              name: record.customername
                ? `${record.customername} ${record.project_name}`
                : `${record.endcustomer} ${record.project_name}`,
            },
            {
              id: record.activity_id,
              name: record.activity_name,
              start_date: record.start_date,
              stop_date: record.stop_date,
              budget_hours: record.budget_hours, // From backend joined data (needs verify if record has it?) 
              // Wait, 'record' here comes from groupedRecords which comes from timeRecords service... 
              // NO! timeRecords service returns time entries. ProjectsActivities returns the structure.
              // We need to merge them or check where 'record' comes from.
            },
          ],
          data: Array(this.periodLength)
            .fill(null)
            .map(() => ({
              time: null,
              previousValue: null,
              id: null,
              statement: null,
              salary: null,
              note: null,
            })),
          statement: record.statement_id,
          salary: record.salary_id,
        });
      }

      const dayIndex = moment(record.reporteddate).diff(this.selectedDate, 'days');
      if (dayIndex >= 0 && dayIndex < this.periodLength) {
        const group = groupedRecords.get(key);
        group.data[dayIndex] = {
          time: record.time,
          previousValue: record.time,
          id: record.record_id,
          statement: record.statement_id,
          salary: record.salary_id,
          note: record.note ?? null,
        };
      }
    });

    this.currTimeTable = Array.from(groupedRecords.values());
    this.currTimeTable.push(this.createEmptyRow());
    console.log('currTimeTable:', this.currTimeTable);
  }

  createEmptyRow() {
    return {
      header: [null, null],
      data: Array(this.periodLength)
        .fill(null)
        .map(() => ({
          time: null,
          previousValue: null,
          id: null,
          statement: null,
          salary: null,
          note: null,
        })),
    };
  }

  fetchData() {
    this.timerecordsService
      .getWeek(this.selectedDate, this.selectedDate.clone().add(this.periodLength - 1, 'day'))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.timeRecords = data;
          console.log('Fetched time records:', this.timeRecords);
          
          if (this.timeRecords.length === 0) {
              console.log('Week is empty, fetching recent activities...');
              this.projectsActivitiesService.getRecent().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                  next: (recentData) => {
                      console.log('Fetched recent activities:', recentData);
                      // Convert recent activities to flat format for buildTimeTable?
                      // buildTimeTable expects grouped records.
                      // OR we can manually build currTimeTable from recentData.
                      // recentData has { id, name, activities: [ { id, name } ] }
                      // We need to create rows for each P/A combo.
                      
                      const simulatedRecords: TimeRecord[] = [];
                      recentData.forEach((p) => {
                          p.activities.forEach((a) => {
                             simulatedRecords.push({
                                 project_id: p.id,
                                 // Use full name in customername and empty project for simplicity in buildTimeTable logic
                                 customername: p.name,
                                 project_name: '',
                                 activity_id: a.id,
                                 activity_name: a.name,
                                 start_date: a.start_date,
                                 stop_date: a.stop_date,
                                 time: null // Null = tomt fält i UI (0 visas som "0" vilket förvirrar)
                             });
                          });
                      });
                      
                      this.buildTimeTable(simulatedRecords);
                      this.updateStats(
                        this.selectedDate,
                        this.selectedDate.clone().add(this.periodLength - 1, 'day')
                      );
                      this.mergeProjectBudgetInfo();
                  },
                  error: (err) => console.error('Error fetching recent', err)
              });
          } else {
              this.buildTimeTable(this.timeRecords);
              this.updateStats(
                this.selectedDate,
                this.selectedDate.clone().add(this.periodLength - 1, 'day')
              );
               this.mergeProjectBudgetInfo();
          }
        },
        error: (error) => {
          console.error('Error fetching time records:', error);
        },
      });

    this.projectsActivitiesService
      .get(
        this.selectedDate,
        this.selectedDate.clone().add(this.periodLength - 1, 'day'),
        this.projectBudgetInTimeEnabled
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.projects = data;
          console.log('Fetched projects:', this.projects);
          this.mergeProjectBudgetInfo();
        },
        error: (error) => {
          console.error('Error fetching projects:', error);
        },
      });
  }

  mergeProjectBudgetInfo() {
    if (!this.projects || this.projects.length === 0 || !this.currTimeTable) return;

    this.currTimeTable.forEach(row => {
       const pHeader = row.header[0];
       if (pHeader) {
          const proj = this.projects.find(p => p.id === pHeader.id);
          if (proj) {
             row.header[0] = {
               ...pHeader,
               budget_hours: proj.budget_hours ?? null,
               used_hours_total: proj.used_hours_total ?? 0,
             };
          }
       }
    }); 
  }

  getTimeSum(from: moment.Moment, to: moment.Moment): Observable<number> {
    return this.timerecordsService.getWeek(from, to).pipe(
      map((data) => data.reduce((sum, r) => sum + r.time, 0)),
      catchError((error) => {
        console.error('Error fetching time records:', error);
        return of(0); // fallback-värde (kan ändras)
      })
    );
  }

  updateStats(from: moment.Moment, to: moment.Moment): void {
    console.log('updateStats:', from, to);
    const periodTotal = this.timeRecords.reduce((sum, r) => sum + r.time, 0);
    this.workHours = this.calculateWorkHours(from, to);

    if (periodTotal <= this.workHours) {
      this.periodStatistics.normalTime = periodTotal;
      this.periodStatistics.overTime = 0;
      this.periodStatistics.missingTime = -(periodTotal - this.workHours);
      this.periodStatistics.normalTimePercent =
        (this.periodStatistics.normalTime / this.workHours) * 100;
      this.periodStatistics.overTimePercent =
        (this.periodStatistics.overTime / this.workHours) * 100;
      this.periodStatistics.missingTimePercent =
        (this.periodStatistics.missingTime / this.workHours) * 100;
    } else {
      this.periodStatistics.normalTime = this.workHours;
      this.periodStatistics.overTime = periodTotal - this.workHours;
      this.periodStatistics.missingTime = 0;
      this.periodStatistics.normalTimePercent =
        (this.periodStatistics.normalTime / periodTotal) * 100;
      this.periodStatistics.overTimePercent = (this.periodStatistics.overTime / periodTotal) * 100;
      this.periodStatistics.missingTimePercent =
        (this.periodStatistics.missingTime / periodTotal) * 100;
    }

    const start = from.clone().startOf('month');
    const end = start.clone().endOf('month');

    this.getTimeSum(start, end).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (total) => {
        const workHours = this.calculateWorkHours(start, end);
        this.monthTotal = {
          month: start.format('MMMM'),
          rapporterat: total,
          total: workHours,
          fardig: (total / workHours) * 100,
        };
      },
      error: (error) => {
        console.error('Error fetching time records:', error);
      },
    });

    const start2 = start.clone().subtract(1, 'month').startOf('month');
    const end2 = start2.clone().endOf('month');

    this.getTimeSum(start2, end2).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (total) => {
        const workHours = this.calculateWorkHours(start2, end2);
        this.lastMonthTotal = {
          month: start2.format('MMMM'),
          rapporterat: total,
          total: workHours,
          fardig: (total / workHours) * 100,
        };
      },
      error: (error) => {
        console.error('Error fetching time records:', error);
      },
    });
  }

  /**
   * Update statistics based on local currTimeTable data without fetching from backend.
   * Use this when we know the changes locally (after insert/update/delete).
   */
  updateStatsLocal(): void {
    // Calculate period total from currTimeTable
    const periodTotal = this.currTimeTable.reduce((sum, row) => {
      return (
        sum +
        row.data.reduce((rowSum, dayData) => {
          return rowSum + (dayData.time || 0);
        }, 0)
      );
    }, 0);

    const from = this.selectedDate;
    const to = this.selectedDate.clone().add(this.periodLength - 1, 'day');
    this.workHours = this.calculateWorkHours(from, to);

    // Calculate period statistics
    if (periodTotal <= this.workHours) {
      this.periodStatistics.normalTime = periodTotal;
      this.periodStatistics.overTime = 0;
      this.periodStatistics.missingTime = -(periodTotal - this.workHours);
      this.periodStatistics.normalTimePercent =
        (this.periodStatistics.normalTime / this.workHours) * 100;
      this.periodStatistics.overTimePercent =
        (this.periodStatistics.overTime / this.workHours) * 100;
      this.periodStatistics.missingTimePercent =
        (this.periodStatistics.missingTime / this.workHours) * 100;
    } else {
      this.periodStatistics.normalTime = this.workHours;
      this.periodStatistics.overTime = periodTotal - this.workHours;
      this.periodStatistics.missingTime = 0;
      this.periodStatistics.normalTimePercent =
        (this.periodStatistics.normalTime / periodTotal) * 100;
      this.periodStatistics.overTimePercent = (this.periodStatistics.overTime / periodTotal) * 100;
      this.periodStatistics.missingTimePercent =
        (this.periodStatistics.missingTime / periodTotal) * 100;
    }

    // Calculate month total from currTimeTable for current month
    const start = from.clone().startOf('month');
    const end = start.clone().endOf('month');

    const monthTotal = this.currTimeTable.reduce((sum, row) => {
      return (
        sum +
        row.data.reduce((rowSum, dayData, dayIndex) => {
          const dayDate = this.weekList[dayIndex].header;
          if (dayDate.isSameOrAfter(start) && dayDate.isSameOrBefore(end)) {
            return rowSum + (dayData.time || 0);
          }
          return rowSum;
        }, 0)
      );
    }, 0);

    const workHours = this.calculateWorkHours(start, end);
    this.monthTotal = {
      month: start.format('MMMM'),
      rapporterat: monthTotal,
      total: workHours,
      fardig: (monthTotal / workHours) * 100,
    };

    console.log('Local stats updated:', {
      periodTotal,
      periodStatistics: this.periodStatistics,
      monthTotal: this.monthTotal,
    });
  }

  calculateWorkHours(from: moment.Moment, to: moment.Moment) {
    return this.dateHolidayService.getWorkHoursBetween(from, to);
  }

  updateWeekNo(diff: number) {
    // Just update the date - datepicker will trigger onDateChange automatically
    this.selectedDate = this.selectedDate.clone().add(diff, 'week');
    this.updateData();
  }

  @HostListener('document:click')
  handleDocumentClick(): void {
    this.closeNoteEditor();
  }

  isLockedCell(dayData: any): boolean {
    return dayData?.statement != null || dayData?.salary != null;
  }

  getLockReason(dayData: any): 'invoice' | 'salary' | null {
    if (dayData?.statement != null) return 'invoice';
    if (dayData?.salary != null) return 'salary';
    return null;
  }

  getCellLockTitle(dayData: any): string {
    const reason = this.getLockReason(dayData);
    if (reason === 'invoice') {
      return 'Tiden är kopplad till en faktura och låst.';
    }
    if (reason === 'salary') {
      return 'Tiden är låst för lönehantering.';
    }
    return '';
  }

  hasNote(dayData: any): boolean {
    if (!this.timeNotesEnabled) {
      return false;
    }
    return typeof dayData?.note === 'string' && dayData.note.trim().length > 0;
  }

  canEditNote(dayData: any): boolean {
    if (!this.timeNotesEnabled) {
      return false;
    }
    return !!dayData?.time && !this.isLockedCell(dayData);
  }

  canOpenNote(dayData: any): boolean {
    if (!this.timeNotesEnabled) {
      return false;
    }
    return this.hasNote(dayData) || this.canEditNote(dayData);
  }

  isNoteEditorOpen(rowIndex: number, dayIndex: number): boolean {
    return this.activeNoteEditor?.rowIndex === rowIndex && this.activeNoteEditor?.dayIndex === dayIndex;
  }

  openNoteEditor(rowIndex: number, dayIndex: number, event: Event): void {
    if (!this.timeNotesEnabled) {
      return;
    }
    event.stopPropagation();
    const rowData = this.currTimeTable?.[rowIndex]?.data?.[dayIndex];
    if (!this.canOpenNote(rowData)) {
      return;
    }
    this.activeNoteEditor = { rowIndex, dayIndex };
    this.activeNoteDraft = rowData?.note ?? '';
    this.activeNotePopoverTop = this.calculateNotePopoverTop(event);
  }

  closeNoteEditor(): void {
    this.activeNoteEditor = null;
    this.activeNoteDraft = '';
    this.activeNotePopoverTop = null;
  }

  async saveNote(rowIndex: number, dayIndex: number, event?: Event): Promise<void> {
    if (!this.timeNotesEnabled) {
      return;
    }
    event?.stopPropagation();
    const rowData = this.currTimeTable?.[rowIndex]?.data?.[dayIndex];
    if (!rowData || !this.canEditNote(rowData)) {
      return;
    }
    const trimmed = this.activeNoteDraft.trim();
    if (!rowData.id) {
      await this.dialogService.alert({
        title: 'Kan inte spara anteckning ännu',
        body: 'Spara tiden först innan du lägger till en anteckning.',
        variant: 'info',
      });
      return;
    }

    try {
      const response = await firstValueFrom(this.timerecordsService.updateNote(rowData.id, trimmed ? trimmed : null));
      rowData.note = response?.note ?? null;
      this.closeNoteEditor();
    } catch (error: any) {
      await this.dialogService.alert({
        title: 'Det gick inte att spara anteckningen',
        body: error?.error?.message || error?.error?.error || 'Försök igen eller kontrollera om tiden är låst.',
        variant: 'danger',
      });
    }
  }

  async clearNote(rowIndex: number, dayIndex: number, event?: Event): Promise<void> {
    if (!this.timeNotesEnabled) {
      return;
    }
    event?.stopPropagation();
    const rowData = this.currTimeTable?.[rowIndex]?.data?.[dayIndex];
    if (!rowData || this.isLockedCell(rowData)) {
      return;
    }
    if (!rowData.id) {
      rowData.note = null;
      this.closeNoteEditor();
      return;
    }

    try {
      const response = await firstValueFrom(this.timerecordsService.updateNote(rowData.id, null));
      rowData.note = response?.note ?? null;
      this.closeNoteEditor();
    } catch (error: any) {
      await this.dialogService.alert({
        title: 'Det gick inte att ta bort anteckningen',
        body: error?.error?.message || error?.error?.error || 'Försök igen eller kontrollera om tiden är låst.',
        variant: 'danger',
      });
    }
  }

  private calculateNotePopoverTop(event: Event): number | null {
    if (typeof window === 'undefined' || window.innerWidth > 480) {
      return null;
    }

    const target = event.currentTarget as HTMLElement | null;
    if (!target) {
      return null;
    }

    const rect = target.getBoundingClientRect();
    const estimatedHeight = 180;
    return Math.min(Math.max(rect.top - 16, 8), window.innerHeight - estimatedHeight - 8);
  }

  isLockedRow(row: any): boolean {
    return Array.isArray(row?.data) && row.data.some((dayData: any) => this.isLockedCell(dayData));
  }

  hasMixedLockState(row: any): boolean {
    if (!Array.isArray(row?.data) || row.data.length === 0) return false;
    const lockedCount = row.data.filter((dayData: any) => this.isLockedCell(dayData)).length;
    return lockedCount > 0 && lockedCount < row.data.length;
  }

  getRowLockTitle(row: any): string {
    if (!this.isLockedRow(row)) return '';
    const lockReasons = new Set(
      row.data.map((dayData: any) => this.getLockReason(dayData)).filter((reason: any) => reason)
    );

    if (lockReasons.size > 1) {
      return 'Vissa dagar på raden är låsta av faktura eller lönehantering.';
    }

    const onlyReason = Array.from(lockReasons)[0];

    if (this.hasMixedLockState(row)) {
      if (onlyReason === 'salary') {
        return 'Vissa dagar på raden är låsta för lönehantering.';
      }
      return 'Vissa dagar på raden är kopplade till en faktura och låsta.';
    }

    if (onlyReason === 'salary') {
      return 'Raden är låst för lönehantering.';
    }
    return 'Raden är kopplad till en faktura och låst.';
  }

  async showLockedTimeAlert(action: 'ändra' | 'ta bort' | 'byta projekt eller aktivitet'): Promise<void> {
    await this.dialogService.alert({
      title: 'Tiden är låst',
      body: `Det går inte att ${action} tid som är kopplad till en faktura eller låst för lönehantering.`,
      buttonText: 'OK',
      variant: 'danger',
    });
  }

  async showTimeMutationError(action: 'ändra' | 'ta bort', error: unknown): Promise<void> {
    const httpError = error as HttpErrorResponse | undefined;
    const backendMessage =
      typeof httpError?.error?.message === 'string'
        ? httpError.error.message
        : typeof httpError?.error?.error === 'string'
          ? httpError.error.error
          : null;

    if (httpError?.status === 409) {
      await this.dialogService.alert({
        title: 'Tiden är låst',
        body:
          backendMessage ||
          `Det går inte att ${action} tid som är kopplad till en faktura eller låst för lönehantering.`,
        buttonText: 'OK',
        variant: 'danger',
      });
      return;
    }

    await this.dialogService.alert({
      title: 'Operationen misslyckades',
      body:
        backendMessage ||
        `Det gick inte att ${action} tiden. Försök igen eller kontrollera serverloggen.`,
      buttonText: 'OK',
      variant: 'danger',
    });
  }

  async openProjectActivityModal(projIndex: number, index: number, val: number): Promise<void> {
    console.log('Opening project selection modal', projIndex, index, val);
    console.log('Projects data:', this.projects);

    if (this.isLockedRow(this.currTimeTable[projIndex])) {
      await this.showLockedTimeAlert('byta projekt eller aktivitet');
      return;
    }

    // Projects data is already in the correct format {id, name, activities[]}
    let availableProjects = this.projects;

    // Get currently selected project and activity (if any)
    const currentHeader = this.currTimeTable[projIndex].header;
    console.log('Current header:', currentHeader);

    let selectedProject: ProjectWithActivities | undefined;
    let selectedActivity: ActivityView | undefined;

    // Get all existing project/activity combinations from OTHER rows
    const existingCombinations = this.currTimeTable
      .filter((row, idx) => idx !== projIndex && row.header[0] && row.header[1])
      .map((row) => ({
        projectId: row.header[0].id,
        activityId: row.header[1].id,
      }));

    console.log('Existing combinations on other rows:', existingCombinations);

    // Filter projects and their activities to exclude combinations on other rows
    availableProjects = this.projects
      .map((project) => ({
        ...project,
        activities: project.activities.filter(
          (activity) =>
            !existingCombinations.some(
              (combo) => combo.projectId === project.id && combo.activityId === activity.id
            )
        ),
      }))
      .filter((project) => project.activities.length > 0);

    console.log('Available projects after filtering:', availableProjects);

    // If this row already has a selection, find and pre-select it
    if (currentHeader[0] && currentHeader[1]) {
      selectedProject = availableProjects.find((p) => p.id === currentHeader[0].id);
      if (selectedProject) {
        selectedActivity = selectedProject.activities.find((a) => a.id === currentHeader[1].id);
      }
      console.log('Pre-selected project:', selectedProject);
      console.log('Pre-selected activity:', selectedActivity);
    }

    console.log('Using projects:', availableProjects);

    const result = (await this.modalService.show(ProjectSelectionModalComponent, {
      headerText: 'Välj Projekt och Aktivitet',
      closeButtonText: 'Avbryt',
      actionButtonText: 'Spara',
      projects: availableProjects,
      selectedProject: selectedProject as any, // Cast to any or modal type if stricter match needed
      selectedActivity: selectedActivity as any,
    })) as ProjectSelectionDialogResult | undefined;

    if (!result) {
      console.log('Modal closed');
      if (!this.currTimeTable[projIndex].header[0] || !this.currTimeTable[projIndex].header[1]) {
        console.log('Reverting unsaved time input for row without project/activity');
        this.currTimeTable[projIndex].data.forEach((dayData: any) => {
          if (dayData.time !== dayData.previousValue) {
            dayData.time = dayData.previousValue;
          }
        });
      }
      return;
    }

    const { project, activity } = result;
    console.log('Selected:', project, activity);

    this.currTimeTable[projIndex].header[0] = {
      id: project.id,
      name: project.name,
    };
    this.currTimeTable[projIndex].header[1] = {
      id: activity.id,
      name: activity.name,
    };

    const proj = this.projects.find((p) => p.id === project.id);
    if (proj) {
      this.currTimeTable[projIndex].header[0] = {
        ...this.currTimeTable[projIndex].header[0],
        budget_hours: proj.budget_hours ?? null,
        used_hours_total: proj.used_hours_total ?? 0,
      };
    }

    console.log('Checking for time records to save/update');
    console.log(this.currTimeTable[projIndex].data);

    this.currTimeTable[projIndex].data.forEach((dayData, dayIndex) => {
      if (dayData.time && dayData.time > 0) {
        const date = this.weekList[dayIndex].header.format('YYYY-MM-DD');

        if (dayData.id) {
          console.log(`Updating record ${dayData.id} at day ${dayIndex} with new activity ${activity.id}`);

          const updateData = {
            time: dayData.time,
            activity_id: activity.id,
          };

          this.timerecordsService.update(dayData.id, updateData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response: any) => {
              console.log(`Successfully updated record ${dayData.id}`);
              dayData.previousValue = dayData.time;
              if (response.statement_id) {
                dayData.statement = response.statement_id;
              }
              if (response.salary_id) {
                dayData.salary = response.salary_id;
              }
            },
            error: async (error) => {
              console.error(`Error updating record ${dayData.id}:`, error);
              await this.showTimeMutationError('ändra', error);
            },
          });
        } else {
          console.log(`Inserting new record at day ${dayIndex} with activity ${activity.id}`);

          const insertData = {
            date: date,
            time: dayData.time,
            project: project.id,
            activity_id: activity.id,
          };

          this.timerecordsService.insert(insertData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (response: any) => {
              console.log(`Successfully inserted record`, response);
              if (response.id) {
                dayData.id = response.id;
              }
              dayData.previousValue = dayData.time;
            },
            error: (error) => {
              console.error(`Error inserting record:`, error);
              dayData.time = dayData.previousValue;
            },
          });
        }
      }
    });

    if (projIndex === this.currTimeTable.length - 1) {
      console.log('Adding new empty row since we just filled the last one');
      this.currTimeTable.push(this.createEmptyRow());
    }

    this.updateStatsLocal();
  }

  roundInput(projIndex: number, index: number, table: any) {
    console.log('Round input');
    console.log(projIndex, index, table);
    console.log(table[projIndex].data[index]);
    console.log(table[projIndex].data[index].time);

    if (table[projIndex].data[index]) {
      let orgValue = table[projIndex].data[index].time;
      const fraction = orgValue - Math.floor(orgValue);
      if (fraction > 0 && fraction < 0.5) {
        orgValue = orgValue - fraction + 0.5;
      } else if (fraction > 0.5) {
        orgValue = orgValue - fraction + 1;
      }
      if (table[projIndex].data[index].time !== orgValue) {
        table[projIndex].data[index].time = orgValue;
        this.addRemoveSaveTimeReportRow(projIndex, index);
      }
    }
  }

  addRemoveSaveTimeReportRow(projIndex: number, index: number) {
    const rowData = this.currTimeTable[projIndex].data[index];

    // Check if value actually changed
    if (rowData.time !== rowData.previousValue) {
      console.log('Value changed from', rowData.previousValue, 'to', rowData.time);

      const header = this.currTimeTable[projIndex].header;

      // If no project/activity selected, open modal to select
      if (!header[0] || !header[1]) {
        console.log('No project/activity selected, opening modal');
        // Store the time value temporarily
        const tempTime = rowData.time;
        // Open modal and continue only if the user selected a project and activity.
        this.openProjectActivityModal(projIndex, index, index);
        return;
      }

      const projectId = header[0].id;
      const activityId = header[1].id;
      const date = this.weekList[index].header.format('YYYY-MM-DD');

      const oldVal = rowData.previousValue || 0;
      const newVal = rowData.time || 0;
      const diff = newVal - oldVal;

      const updateProjectBudgetUsageLocal = (difference: number) => {
        const projectId = this.currTimeTable[projIndex]?.header?.[0]?.id;
        if (!projectId) return;
        this.currTimeTable.forEach((row) => {
          const projectHeader = row?.header?.[0];
          if (projectHeader?.id === projectId && typeof projectHeader.used_hours_total === 'number') {
            projectHeader.used_hours_total += difference;
          }
        });
      };

      // If time is null or 0, delete the record
      if (!rowData.time) {
        if (rowData.id) {
          if (this.isLockedCell(rowData)) {
            rowData.time = rowData.previousValue;
            this.showLockedTimeAlert('ta bort');
            return;
          }
          this.timerecordsService.delete(rowData.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: () => {
              console.log('Record deleted successfully');
              rowData.id = null;
              rowData.previousValue = null;
              rowData.note = null;
              updateProjectBudgetUsageLocal(diff);
              this.updateStatsLocal();
            },
            error: async (error) => {
              console.error('Error deleting record:', error);
              rowData.time = rowData.previousValue;
              await this.showTimeMutationError('ta bort', error);
            },
          });
        }
        rowData.note = null;
        if (this.isNoteEditorOpen(projIndex, index)) {
          this.closeNoteEditor();
        }
        return;
      }

      // Prepare data for insert or update
      const data = {
        date: date,
        time: rowData.time,
        activity_id: activityId,
      };

      // Insert new record or update existing
      if (!rowData.id) {
        // Insert new record
          this.timerecordsService.insert(data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (result: any) => {
              console.log('Record inserted successfully', result);
              if (result.id) {
                rowData.id = result.id;
            }
            rowData.previousValue = rowData.time;
            updateProjectBudgetUsageLocal(diff);
            this.updateStatsLocal();
            },
            error: async (error) => {
              console.error('Error inserting record:', error);
              rowData.time = rowData.previousValue;
              await this.showTimeMutationError('ändra', error);
            },
        });
      } else {
        // Update existing record
        if (this.isLockedCell(rowData)) {
          rowData.time = rowData.previousValue;
          this.showLockedTimeAlert('ändra');
          return;
        }
        this.timerecordsService.update(rowData.id, data).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (result: any) => {
            console.log('Record updated successfully', result);
            rowData.previousValue = rowData.time;
            updateProjectBudgetUsageLocal(diff);
            this.updateStatsLocal();
          },
          error: async (error) => {
            console.error('Error updating record:', error);
            rowData.time = rowData.previousValue;
            await this.showTimeMutationError('ändra', error);
          },
        });
      }
    }
  }

  outOfRange(a: any, b: any, c: any) {
    return false;
  }

  hasProjectBudget(projectHeader: any): boolean {
    return this.projectBudgetInTimeEnabled && typeof projectHeader?.budget_hours === 'number';
  }

  getProjectBudgetMeta(projectHeader: any): string {
    if (!this.hasProjectBudget(projectHeader)) {
      return '';
    }

    const used = Number(projectHeader.used_hours_total || 0);
    const budget = Number(projectHeader.budget_hours || 0);
    const over = used - budget;
    if (over > 0) {
      return `${used} / ${budget} h (+${over} h)`;
    }

    const ratio = budget > 0 ? used / budget : 0;
    if (ratio >= 0.85) {
      const remaining = budget - used;
      return `${used} / ${budget} h (${remaining} h kvar)`;
    }

    return `${used} / ${budget} h`;
  }

  getProjectBudgetMetaClass(projectHeader: any): string {
    if (!this.hasProjectBudget(projectHeader)) {
      return '';
    }

    const used = Number(projectHeader.used_hours_total || 0);
    const budget = Number(projectHeader.budget_hours || 0);
    if (budget <= 0) {
      return '';
    }

    const ratio = used / budget;
    if (ratio > 1) {
      return 'time-project-budget-meta-over';
    }
    if (ratio >= 0.85) {
      return 'time-project-budget-meta-warning';
    }
    return 'time-project-budget-meta-normal';
  }

  getProjectBudgetMetaTitle(projectHeader: any): string {
    if (!this.hasProjectBudget(projectHeader)) {
      return '';
    }

    const used = Number(projectHeader.used_hours_total || 0);
    const budget = Number(projectHeader.budget_hours || 0);
    const over = used - budget;

    if (over > 0) {
      return `Budget ${budget} h, använt ${used} h, över med ${over} h`;
    }

    return `Budget ${budget} h, använt ${used} h`;
  }
}
